import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';

import { ArcadeChipPng } from '../../../assets/bonuses';
import { EquippedCheckSvg, FrameArtworkPng, ItemNoneSvg } from '../../../assets/inventory';
import { Pressable } from '../common/Touchable';
import { groupThousands } from '../Event/eventTheme';
import { InventoryItem } from './inventoryCatalog';
import { INVENTORY_FONT, inventoryColors, RADIUS_CARD } from './inventoryTheme';

/**
 * Figma "Item / None" (4038:30493), "Item / Classic / Equipped" (4038:30500) and "Item / Royal"
 * (4038:30510) - the three states one 106x132 cell of the grid can be in. All share the panel's
 * `surface/raised` fill and a 10 radius; what changes is the outline and what sits under the name:
 *
 *   - resting (owned or priced): the duller `#DA600E` hairline, no glow;
 *   - equipped: the bright `#FCF17B` hairline with `0 0 10 #FF7200` around it, a green tick and
 *     "Equipped" where the price would be;
 *   - none: the same resting outline, the slash-circle artwork, and a muted name with no price.
 */
export const ITEM_DESIGN_WIDTH = 106;
export const ITEM_DESIGN_HEIGHT = 132;

const PADDING_Y = 8;
const GAP = 4;
const ART_SIZE = 76;
const NAME_SIZE = 14;
const CHECK_SIZE = 20;
const CHIP_SIZE = 14;
/** Where the design pins the equipped tick and its label. */
const EQUIPPED_TOP = 102;
/** The design's box is 14; a 14 line box on 14px bold clips descenders, so it gets 2 of air. */
const NAME_LINE = 16;

type Props = {
	item: InventoryItem;
	scale: number;
	onPress?: () => void;
};

export const InventoryItemCard = ({ item, scale, onPress }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const isEquipped = item.state === 'equipped';
	const isNone = item.state === 'none';

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityState={{ selected: isEquipped }}
			onPress={onPress}
			style={[styles.cell, isEquipped ? styles.cellEquipped : styles.cellResting]}
		>
			{isNone ? (
				<ItemNoneSvg width={ART_SIZE * scale} height={ART_SIZE * scale} />
			) : (
				<Image
					source={item.artwork ?? FrameArtworkPng}
					style={styles.artwork}
					resizeMode='contain'
				/>
			)}

			<Text
				numberOfLines={1}
				adjustsFontSizeToFit
				style={[styles.name, isNone ? styles.nameMuted : styles.nameGold]}
			>
				{t(item.nameKey, { defaultValue: item.name })}
			</Text>

			{isEquipped ? (
				<View pointerEvents='none' style={styles.equippedRow}>
					<EquippedCheckSvg width={CHECK_SIZE * scale} height={CHECK_SIZE * scale} />
					<Text numberOfLines={1} style={styles.equippedLabel}>
						{t('T_SITE_INVENTORY_EQUIPPED', { defaultValue: 'Equipped' })}
					</Text>
				</View>
			) : null}

			{item.amount != null ? (
				<Text numberOfLines={1} style={styles.amountLabel}>
					{t('T_SITE_INVENTORY_AMOUNT', {
						defaultValue: 'Amount: {{amount}}',
						amount: groupThousands(item.amount),
					})}
				</Text>
			) : null}

			{item.state === 'locked' && item.price != null ? (
				<View style={styles.priceRow}>
					<Image source={ArcadeChipPng} style={styles.chip} resizeMode='contain' />
					<Text numberOfLines={1} style={styles.priceLabel}>
						{groupThousands(item.price)}
					</Text>
				</View>
			) : null}
		</Pressable>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		cell: {
			width: ITEM_DESIGN_WIDTH * scale,
			height: ITEM_DESIGN_HEIGHT * scale,
			alignItems: 'center',
			paddingVertical: PADDING_Y * scale,
			gap: GAP * scale,
			borderRadius: RADIUS_CARD * scale,
			borderWidth: Math.max(1, 1 * scale),
			backgroundColor: inventoryColors.surface,
			overflow: 'hidden',
		},
		cellResting: {
			borderColor: inventoryColors.itemBorder,
		},
		cellEquipped: {
			borderColor: inventoryColors.itemBorderEquipped,
			shadowColor: '#FF7200',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 1,
			shadowRadius: 10,
			elevation: 6,
		},
		artwork: {
			width: ART_SIZE * scale,
			height: ART_SIZE * scale,
		},
		// Figma "Button/Label": Sofia Sans Bold 14, line-height 14.
		name: {
			maxWidth: (ITEM_DESIGN_WIDTH - 8) * scale,
			fontFamily: INVENTORY_FONT,
			fontSize: NAME_SIZE * scale,
			fontWeight: '700',
			letterSpacing: 0.0547 * scale,
			lineHeight: NAME_LINE * scale,
			textAlign: 'center',
		},
		nameGold: {
			color: inventoryColors.gold50,
		},
		nameMuted: {
			color: inventoryColors.muted,
		},
		// Figma pins this block rather than stacking it: "absolute left-[16px] top-[102px]". In the
		// flow its 20-tall tick added 24 to a column that only had 132 to give, so the cell
		// overflowed and `overflow: hidden` shaved the name above it.
		equippedRow: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: EQUIPPED_TOP * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 2 * scale,
		},
		// Figma "Label/Tiny": Sofia Sans SemiBold 10, line-height 12, in the progress green.
		equippedLabel: {
			color: inventoryColors.equipped,
			fontFamily: INVENTORY_FONT,
			fontSize: 10 * scale,
			fontWeight: '600',
			lineHeight: 12 * scale,
		},
		priceRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 4 * scale,
		},
		chip: {
			width: CHIP_SIZE * scale,
			height: CHIP_SIZE * scale,
		},
		// The price's type, so a ticket stack reads like the priced items beside it.
		amountLabel: {
			color: inventoryColors.accent,
			fontFamily: INVENTORY_FONT,
			fontSize: 14 * scale,
			fontWeight: '600',
			lineHeight: 14.13 * scale,
		},
		// Figma "Numeric/Small": Sofia Sans SemiBold 14, in text/accent.
		priceLabel: {
			color: inventoryColors.accent,
			fontFamily: INVENTORY_FONT,
			fontSize: 14 * scale,
			fontWeight: '600',
			lineHeight: 14.13 * scale,
		},
	});

export default InventoryItemCard;
